import React, { useState } from 'react';
import styled from 'styled-components';
import api from '../../services/api';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
`;

const Title = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #333;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InputField = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
  &:focus {
    border-color: #ffc003;
  }
`;

const ErrorMsg = styled.p`
  font-size: 0.8rem;
  color: #e53935;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const BtnSkip = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  color: #888;
  padding: 8px 12px;
`;

const BtnSave = styled.button`
  background: #ffc003;
  color: #1a1a2e;
  border: none;
  border-radius: 6px;
  padding: 10px 24px;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  &:hover {
    background: #e6ac00;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const ForceChangePasswordModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    setError('');
    if (password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/change-my-password', {
        password,
        confirmPassword: confirm,
      });

      if (data.result === 'success') {
        sessionStorage.removeItem('mustChangePassword');
        onClose();
      } else {
        setError(data.message || 'Erro ao alterar a senha.');
      }
    } catch {
      setError('Erro ao alterar a senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    sessionStorage.removeItem('mustChangePassword');
    onClose();
  };

  return (
    <Overlay>
      <Card>
        <Title>Crie sua senha personalizada</Title>
        <Subtitle>
          Você acessou com uma senha gerada automaticamente. Recomendamos criar
          sua própria senha agora. Você pode fazer isso depois se preferir.
        </Subtitle>

        <Label>
          Nova senha
          <InputField
            type="password"
            placeholder="Mínimo 8 caracteres"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Label>

        <Label>
          Confirmar nova senha
          <InputField
            type="password"
            placeholder="Repita a senha"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </Label>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <Actions>
          <BtnSkip type="button" onClick={handleSkip}>
            Agora não
          </BtnSkip>
          <BtnSave type="button" onClick={handleSave} disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar senha'}
          </BtnSave>
        </Actions>
      </Card>
    </Overlay>
  );
};

export default ForceChangePasswordModal;
